import jsPDF from 'jspdf';
import React, { useEffect, useRef } from 'react';
import uplatnica from "src/assets/images/uplatnica.jpg";
import { formatNumber, ProductTypeCodeEnum } from 'src/common';
import { BarCode } from 'src/components';
import { useGetGeneralTransferPaymentDetailsQuery } from '../api/apollo/additional';

export const usePaymentSlipAdditional = (offerId: string, paymentType: "oneTime" | "monthly") => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barcodeRef = useRef<HTMLCanvasElement | null>(null);
  const isCanvasDrawn = useRef(false);

  const { loading, data } = useGetGeneralTransferPaymentDetailsQuery({
    variables: { id: offerId, input: { installments: paymentType === "oneTime" ? 1 : 12, type: ProductTypeCodeEnum.DODATNO } },
    fetchPolicy: 'cache-first',
  });


  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.getGeneralTransferPaymentDetails || !barcodeRef.current || isCanvasDrawn.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const gtpd = data.getGeneralTransferPaymentDetails;
    const rawPrice = gtpd.price;
    const price =
      rawPrice != null
        ? formatNumber(paymentType === 'oneTime' ? rawPrice : rawPrice / 12)
        : '';
    const innerPrice = `=${price}`;
    const outerPrice = `=${price} EUR`;

    const customerFullName = data?.getGeneralTransferPaymentDetails?.customerFirstName && data?.getGeneralTransferPaymentDetails?.customerLastName ? `${data?.getGeneralTransferPaymentDetails?.customerFirstName} ${data?.getGeneralTransferPaymentDetails?.customerLastName}` : 'Ime i prezime';
    const customerAddress = data?.getGeneralTransferPaymentDetails?.customerAddress || 'Ulica i broj';
    const customerZipCode = data?.getGeneralTransferPaymentDetails?.customerZipCode && data?.getGeneralTransferPaymentDetails?.customerZipCode.toString() !== '0' ? data?.getGeneralTransferPaymentDetails?.customerZipCode.toString() : 'Pošt. br.';
    const customerCity = data?.getGeneralTransferPaymentDetails?.customerCity || 'Grad';
    const customerCountry = data?.getGeneralTransferPaymentDetails?.customerCountry || 'Hrvatska';

    const recipientName = data?.getGeneralTransferPaymentDetails?.recipientName || '';
    const recipientAddress = data?.getGeneralTransferPaymentDetails?.recipientAddress || '';
    const recipientCity = data?.getGeneralTransferPaymentDetails?.recipientCity || '';
    const recipientZipCode = data?.getGeneralTransferPaymentDetails?.recipientZipCode || '';
    const iban = data?.getGeneralTransferPaymentDetails?.recipientAccountNumber || '';

    const description = data?.getGeneralTransferPaymentDetails?.description || 'Uplata dopunskog osiguranja';

    const referenceNumber = data?.getGeneralTransferPaymentDetails?.referenceNumber || data?.getGeneralTransferPaymentDetails?.referenceNumberType || 'Vaš oib';

    const model = data?.getGeneralTransferPaymentDetails?.model || '';

    const prefix = data?.getGeneralTransferPaymentDetails?.referencePrefix || null;
    const prefixLength = prefix ? prefix.toString().length : 0;
    const prefixPosition = prefixLength > 6 ? 1000 : 1020;

    const modelAndReferenceNumber = `${model} - ${prefix ?? ''}${referenceNumber}`;

    const imageObj = new Image();
    imageObj.onload = function () { // eslint-disable-line func-names
      ctx.drawImage(imageObj, 1, 2, 1920, 806);

      ctx.font = '32px Arial';
      ctx.fillText(customerFullName, 75, 130);
      ctx.fillText(customerAddress, 75, 165);
      ctx.fillText(customerZipCode, 75, 200);
      ctx.fillText(customerCity, 200, 200);
      ctx.fillText(customerCountry, 75, 230);

      ctx.fillText(recipientName, 75, 365);
      ctx.fillText(recipientAddress, 75, 400);
      ctx.fillText(recipientCity, 200, 435);
      ctx.fillText(recipientZipCode.toString(), 75, 435);
      ctx.fillText('Hrvatska', 75, 465);
      ctx.fillText(iban, 935, 285);

      ctx.fillText(model, 515, 355);
      if (prefix) {
        ctx.fillText(prefix, prefixPosition, 355);
      }
      ctx.fillText(referenceNumber, 1120, 355);
      ctx.fillText(description, 820, 400);

      ctx.fillText(innerPrice, 1195, 100);

      ctx.fillText(outerPrice, 1680, 100);
      ctx.fillText(iban, 1460, 285);
      ctx.fillText(modelAndReferenceNumber, 1460, 355);
      ctx.fillText(description, 1400, 420);

      if (barcodeRef.current) {
        ctx.drawImage(barcodeRef.current, 70, 550, 500, 166);
      }

      isCanvasDrawn.current = true;
    };

    imageObj.src = uplatnica;
  };

  useEffect(() => {
    if (!loading && data?.getGeneralTransferPaymentDetails && barcodeRef.current && !isCanvasDrawn.current) {
      drawCanvas();
    }
  }, [loading, data?.getGeneralTransferPaymentDetails, barcodeRef.current]);

  const downloadSlip = () => {
    if (!canvasRef.current) return;

    const pdfPrep = canvasRef.current.toDataURL('image/jpeg', 1.0);
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px' });
    pdf.addImage(pdfPrep, 'JPEG', 0, 0, 450, 210);
    pdf.save('uplatnica-dodatnog-osiguranja.pdf');
  };

  const PaymentSlip: React.FC = () => {
    const gtpd = data?.getGeneralTransferPaymentDetails;
    const rawPrice = gtpd?.price;
    const price =
      rawPrice != null
        ? formatNumber(paymentType === 'oneTime' ? rawPrice : rawPrice / 12)
        : '';

    const handleBarcodeGenerated = (canvas: HTMLCanvasElement) => {
      barcodeRef.current = canvas;
    };

    return (
      <>
        <canvas
          ref={canvasRef}
          id="generalPaymentCanvas"
          data-testid="generalPaymentCanvas"
          width="1920"
          height="806"
          style={{ display: 'none' }}
        />
        <div style={{ display: 'none' }}>
          <BarCode
            price={price}
            userFirstName={data?.getGeneralTransferPaymentDetails?.customerFirstName || ''}
            userLastName={data?.getGeneralTransferPaymentDetails?.customerLastName || ''}
            userAddress={data?.getGeneralTransferPaymentDetails?.customerAddress || ''}
            userCity={data?.getGeneralTransferPaymentDetails?.customerCity || ''}
            oib={data?.getGeneralTransferPaymentDetails?.customerOib || ''}
            recipient={data?.getGeneralTransferPaymentDetails?.recipientName || ''}
            recipientAddress={data?.getGeneralTransferPaymentDetails?.recipientAddress || ''}
            recipientCity={data?.getGeneralTransferPaymentDetails?.recipientCity || ''}
            model={data?.getGeneralTransferPaymentDetails?.model || ''}
            iban={data?.getGeneralTransferPaymentDetails?.recipientAccountNumber || ''}
            description={data?.getGeneralTransferPaymentDetails?.description || 'Uplata dopunskog osiguranja'}
            hidden={false}
            onBarcodeGenerated={handleBarcodeGenerated}
          />
        </div>
      </>
    )
  };

  return {
    downloadSlip,
    loading,
    PaymentSlip,
  };
};
